/**
 * InquiryConfirmation Component - Story 7.4: Create Inquiry Submission Flow
 * Confirmation page displayed after successful inquiry submission
 *
 * Features:
 * - Success icon and confirmation message
 * - Inquiry details display (ID, product info if available)
 * - Information about next steps
 * - Multiple navigation options (Browse, View Inquiries, Home)
 * - Responsive design for mobile and desktop
 * - Accessible with WCAG 2.1 AA compliance
 * - Smooth animations and transitions
 *
 * @example
 * ```tsx
 * // Via route state (preferred)
 * navigate('/marketplace/inquiry/confirmation', {
 *   state: {
 *     inquiryId: 'INQ-12345',
 *     productName: 'Biogas Digester',
 *     productCategory: 'EQUIPMENT'
 *   }
 * });
 *
 * // Or as props
 * <InquiryConfirmation
 *   inquiryId="INQ-12345"
 *   productName="Biogas Digester"
 *   productCategory="EQUIPMENT"
 * />
 * ```
 */

import React, { useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Separator } from '@/components/ui/separator';
import { cn } from '@/lib/utils';
import {
  CheckCircle,
  Home,
  ShoppingBag,
  Mail,
  Info,
  ArrowRight,
  Package,
  Clock,
  UserCheck,
} from 'lucide-react';

// ============================================================================
// Constants
// ============================================================================

/**
 * Page metadata
 */
const PAGE_TITLE = 'Inquiry Submitted - SAUBHAGYA Marketplace';
const PAGE_DESCRIPTION = 'Your product inquiry has been submitted successfully. The seller will contact you soon.';

/**
 * Confirmation messages
 */
const SUCCESS_TITLE = 'Inquiry Submitted Successfully!';
const SUCCESS_MESSAGE = 'Seller will contact you soon';
const SUCCESS_DESCRIPTION =
  'Your inquiry has been sent to the seller. They will review your request and get back to you via email or phone within 1-2 business days.';

/**
 * Next steps information
 */
const NEXT_STEPS = [
  {
    icon: UserCheck,
    title: 'Seller Review',
    description: 'The seller will review your inquiry and requirements',
    color: 'text-blue-600',
    bgColor: 'bg-blue-50',
  },
  {
    icon: Mail,
    title: 'Contact Initiated',
    description: 'Seller will reach out via your provided email or phone',
    color: 'text-green-600',
    bgColor: 'bg-green-50',
  },
  {
    icon: Package,
    title: 'Details Discussion',
    description: 'Discuss pricing, delivery, and finalize the purchase',
    color: 'text-purple-600',
    bgColor: 'bg-purple-50',
  },
] as const;

/**
 * Expected response time
 */
const RESPONSE_TIME = '1-2 business days';

// ============================================================================
// Type Definitions
// ============================================================================

/**
 * Props interface for InquiryConfirmation component
 */
export interface InquiryConfirmationProps {
  /**
   * Inquiry ID (optional - can be passed via route state or props)
   */
  inquiryId?: string;

  /**
   * Product name (optional)
   */
  productName?: string;

  /**
   * Product category (optional)
   */
  productCategory?: string;

  /**
   * Custom className for root element
   */
  className?: string;

  /**
   * Hide navigation buttons (for testing)
   */
  hideNavigation?: boolean;
}

/**
 * Route state type for inquiry confirmation
 */
interface InquiryConfirmationState {
  inquiryId?: string;
  productName?: string;
  productCategory?: string;
}

// ============================================================================
// Sub-Components
// ============================================================================

/**
 * Success Hero Section
 */
const SuccessHero: React.FC<{
  inquiryId?: string;
}> = ({ inquiryId }) => (
  <div className="text-center space-y-6 py-8">
    {/* Success Icon */}
    <div className="flex justify-center">
      <div
        className={cn(
          'relative',
          'w-24 h-24 md:w-32 md:h-32',
          'rounded-full',
          'bg-gradient-to-br from-green-50 to-green-100',
          'flex items-center justify-center',
          'shadow-lg',
          'animate-in zoom-in duration-500'
        )}
      >
        <CheckCircle
          className="w-12 h-12 md:w-16 md:h-16 text-green-600"
          strokeWidth={2}
          aria-hidden="true"
        />
        {/* Animated ring */}
        <div className="absolute inset-0 rounded-full border-4 border-green-300 animate-ping opacity-20" />
      </div>
    </div>

    {/* Success Message */}
    <div className="space-y-2 animate-in fade-in slide-in-from-bottom-4 duration-700 delay-150">
      <h1 className="text-3xl md:text-4xl font-bold text-gray-900">{SUCCESS_TITLE}</h1>
      <p className="text-xl md:text-2xl text-green-600 font-semibold">{SUCCESS_MESSAGE}</p>
    </div>

    {/* Inquiry ID Badge */}
    {inquiryId && (
      <div className="inline-flex items-center gap-2 px-4 py-2 bg-blue-50 border border-blue-200 rounded-full animate-in fade-in duration-700 delay-300">
        <Info className="w-4 h-4 text-blue-600" aria-hidden="true" />
        <span className="text-sm font-medium text-blue-900">
          Inquiry ID: <span className="font-bold">{inquiryId}</span>
        </span>
      </div>
    )}
  </div>
);

/**
 * Inquiry Details Card
 */
const InquiryDetailsCard: React.FC<{
  productName?: string;
  productCategory?: string;
}> = ({ productName, productCategory }) => {
  if (!productName && !productCategory) return null;

  return (
    <Card className="animate-in fade-in slide-in-from-bottom-4 duration-700 delay-200">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-lg">
          <Package className="w-5 h-5 text-blue-600" aria-hidden="true" />
          Inquiry Details
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        {productName && (
          <div>
            <p className="text-sm text-gray-600 mb-1">Product</p>
            <p className="text-base font-semibold text-gray-900">{productName}</p>
          </div>
        )}
        {productCategory && (
          <div>
            <p className="text-sm text-gray-600 mb-1">Category</p>
            <p className="text-base font-medium text-gray-700">{productCategory}</p>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

/**
 * What Happens Next Section
 */
const NextStepsSection: React.FC = () => (
  <Card className="animate-in fade-in slide-in-from-bottom-4 duration-700 delay-300">
    <CardHeader>
      <CardTitle className="flex items-center gap-2">
        <Clock className="w-5 h-5 text-purple-600" aria-hidden="true" />
        What Happens Next?
      </CardTitle>
      <CardDescription>Here's what you can expect after submitting your inquiry</CardDescription>
    </CardHeader>
    <CardContent>
      <div className="space-y-4">
        {NEXT_STEPS.map((step, index) => {
          const Icon = step.icon;
          return (
            <div key={index} className="flex gap-4">
              {/* Step Icon */}
              <div className={cn('flex-shrink-0 p-3 rounded-lg', step.bgColor)}>
                <Icon className={cn('w-5 h-5', step.color)} aria-hidden="true" />
              </div>

              {/* Step Content */}
              <div className="flex-1 space-y-1">
                <h3 className="font-semibold text-gray-900">{step.title}</h3>
                <p className="text-sm text-gray-600">{step.description}</p>
              </div>

              {/* Step Number */}
              <div className="flex-shrink-0 w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center">
                <span className="text-sm font-bold text-gray-600">{index + 1}</span>
              </div>
            </div>
          );
        })}
      </div>

      <Separator className="my-6" />

      {/* Response Time Alert */}
      <Alert className="border-blue-200 bg-blue-50">
        <Clock className="w-5 h-5 text-blue-600" />
        <AlertDescription className="text-blue-900">
          <span className="font-semibold">Expected Response Time:</span> {RESPONSE_TIME}
          <br />
          <span className="text-sm mt-1 inline-block">
            Please check your email and phone for updates from the seller.
          </span>
        </AlertDescription>
      </Alert>
    </CardContent>
  </Card>
);

/**
 * Navigation Actions Section
 */
const NavigationActions: React.FC<{
  onBrowseProducts: () => void;
  onGoHome: () => void;
  onViewInquiries?: () => void;
  showViewInquiries?: boolean;
}> = ({ onBrowseProducts, onGoHome, onViewInquiries, showViewInquiries = false }) => (
  <div className="space-y-4 animate-in fade-in slide-in-from-bottom-4 duration-700 delay-400">
    {/* Primary CTA - Browse More Products */}
    <Button
      onClick={onBrowseProducts}
      size="lg"
      className={cn(
        'w-full h-14',
        'bg-blue-600 hover:bg-blue-700',
        'text-white font-semibold text-lg',
        'rounded-xl shadow-lg hover:shadow-xl',
        'transition-all duration-300',
        'group'
      )}
    >
      <ShoppingBag className="mr-2 w-5 h-5" aria-hidden="true" />
      Browse More Products
      <ArrowRight
        className={cn(
          'ml-2 w-5 h-5',
          'transition-transform duration-300',
          'group-hover:translate-x-1'
        )}
        aria-hidden="true"
      />
    </Button>

    {/* Secondary Actions */}
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
      {/* Back to Home */}
      <Button
        onClick={onGoHome}
        variant="outline"
        size="lg"
        className={cn(
          'h-12',
          'border-2 border-gray-300 hover:border-gray-400',
          'hover:bg-gray-50',
          'transition-all duration-200'
        )}
      >
        <Home className="mr-2 w-5 h-5" aria-hidden="true" />
        Back to Home
      </Button>

      {/* View My Inquiries (conditional) */}
      {showViewInquiries && onViewInquiries && (
        <Button
          onClick={onViewInquiries}
          variant="outline"
          size="lg"
          className={cn(
            'h-12',
            'border-2 border-blue-300 hover:border-blue-400',
            'text-blue-600 hover:bg-blue-50',
            'transition-all duration-200'
          )}
        >
          <Mail className="mr-2 w-5 h-5" aria-hidden="true" />
          View My Inquiries
        </Button>
      )}
    </div>
  </div>
);

// ============================================================================
// Main Component
// ============================================================================

/**
 * InquiryConfirmation - Confirmation page after inquiry submission
 *
 * Displays success message, inquiry details, next steps information,
 * and navigation options for the user.
 */
export const InquiryConfirmation: React.FC<InquiryConfirmationProps> = ({
  inquiryId: propInquiryId,
  productName: propProductName,
  productCategory: propProductCategory,
  className,
  hideNavigation = false,
}) => {
  // ============================================================================
  // Hooks
  // ============================================================================

  const navigate = useNavigate();
  const location = useLocation();

  // Extract data from route state (preferred) or props (fallback)
  const state = location.state as InquiryConfirmationState | null;
  const inquiryId = state?.inquiryId || propInquiryId;
  const productName = state?.productName || propProductName;
  const productCategory = state?.productCategory || propProductCategory;

  // ============================================================================
  // Effects
  // ============================================================================

  /**
   * Scroll to top on mount
   */
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  // ============================================================================
  // Event Handlers
  // ============================================================================

  /**
   * Navigate to product browse page
   */
  const handleBrowseProducts = (): void => {
    navigate('/marketplace/products');
  };

  /**
   * Navigate to marketplace home
   */
  const handleGoHome = (): void => {
    navigate('/marketplace/home');
  };

  /**
   * Navigate to inquiry inbox (seller only)
   * In a real app, check user role before showing this option
   */
  const handleViewInquiries = (): void => {
    navigate('/marketplace/seller/inquiries');
  };

  // ============================================================================
  // Render
  // ============================================================================

  return (
    <>
      {/* SEO Metadata */}
      <Helmet>
        <title>{PAGE_TITLE}</title>
        <meta name="description" content={PAGE_DESCRIPTION} />
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>

      {/* Main Content */}
      <main
        className={cn(
          'min-h-screen w-full bg-gradient-to-br from-gray-50 to-blue-50',
          className
        )}
        role="main"
      >
        {/* Container */}
        <div className="container mx-auto px-4 py-8 md:py-12 max-w-3xl">
          {/* Success Hero */}
          <SuccessHero inquiryId={inquiryId} />

          {/* Main Description */}
          <div className="text-center mb-8 animate-in fade-in duration-700 delay-100">
            <p className="text-base md:text-lg text-gray-700 leading-relaxed max-w-2xl mx-auto">
              {SUCCESS_DESCRIPTION}
            </p>
          </div>

          {/* Content Cards */}
          <div className="space-y-6">
            {/* Inquiry Details Card (if available) */}
            <InquiryDetailsCard productName={productName} productCategory={productCategory} />

            {/* Next Steps Card */}
            <NextStepsSection />

            {/* Navigation Actions */}
            {!hideNavigation && (
              <NavigationActions
                onBrowseProducts={handleBrowseProducts}
                onGoHome={handleGoHome}
                onViewInquiries={handleViewInquiries}
                showViewInquiries={false} // Set to true if user is a seller
              />
            )}
          </div>

          {/* Footer Note */}
          <div className="text-center mt-8 pt-6 border-t border-gray-200 animate-in fade-in duration-1000 delay-500">
            <p className="text-sm text-gray-500">
              Need help? Contact our{' '}
              <a
                href="/support"
                className="text-blue-600 hover:text-blue-700 underline font-medium"
              >
                support team
              </a>
              .
            </p>
          </div>
        </div>
      </main>
    </>
  );
};

export default InquiryConfirmation;
